import React, { useMemo, useState } from 'react';

import { IconX } from '@tabler/icons-react';
import { produce } from 'immer';

import useReactGA from 'hooks/useReactGA';
import { ProfileErrorMessages } from 'pages/Settings/GeoConfigSettings';
import {
  PostGeoConfig,
  PostGeoConfigLocation,
} from 'pages/Settings/GeoConfigSettings/api/useGeoConfig/useGeoConfig';
import useSaveGeoConfig from 'pages/Settings/GeoConfigSettings/api/useSaveGeoConfig/useSaveGeoConfig';
import CreatNewPin from 'pages/Settings/GeoConfigSettings/NewProfileSidebar/CreatNewPin';
import PinCard from 'pages/Settings/GeoConfigSettings/NewProfileSidebar/PinCard';
import { useGeoConfigContext } from 'pages/Settings/GeoConfigSettings/utils/GeoConfigContext';
import Button from '@lotlinx-aws/sparky-component-library/Sparky/Button';
import IconButton from '@lotlinx-aws/sparky-component-library/Sparky/IconButton';
import Message from '@lotlinx-aws/sparky-component-library/Sparky/Message';
import { PlusIcon } from '@lotlinx-aws/sparky-component-library/Sparky/SVGs/PlusIcon';
import Text from '@lotlinx-aws/sparky-component-library/Sparky/Text';
import TextInputField from '@lotlinx-aws/sparky-component-library/Sparky/TextInputField';
import { useToast } from '@lotlinx-aws/sparky-component-library/Sparky/Toast';

import { defaultProfile } from './defaultProfile';
import styles from './NewProfileSidebar.module.scss';

export interface NewProfileSidebarProps {
  refetch(): void;
  defaultGeoConfig?: PostGeoConfig;
  onSelectPin(location: PostGeoConfigLocation): void;
  onCloseCreateNewProfile(): void;
  onSaveCreateNewProfile(): void;
}

/**
 * NewProfileSidebar
 */
export default function NewProfileSidebar({
  refetch,
  defaultGeoConfig,
  onSelectPin,
  onCloseCreateNewProfile,
  onSaveCreateNewProfile,
}: NewProfileSidebarProps) {
  const [isAddingPin, setIsAddingPin] = useState(true);
  const [isModifyingPin, setIsModifyingPin] = useState(false);
  const { addReactGA } = useReactGA();
  const {
    geoConfigProfileNames,
    lastSavedTempGeoConfig,
    setLastSavedTempGeoConfig,
    tempGeoConfigs,
    setTempGeoConfigs,
    selectProfileIndex,
    selectPinIndex,
    setSelectPin,
    editingPin,
    setEditingPin,
    selectProfile,
    setSelectProfile,
    errorMessage,
    setErrorMessage,
    setCreatingNewProfile,
    onLongChange,
    onRadiusChange,
    onLatChange,
  } = useGeoConfigContext();

  const profileNameLength = useMemo(
    () => tempGeoConfigs[selectProfileIndex].name.length ?? 0,
    [tempGeoConfigs, selectProfileIndex],
  );

  const pinsLength = useMemo(
    () => tempGeoConfigs[selectProfileIndex].locations.length ?? 0,
    [tempGeoConfigs, selectProfileIndex],
  );

  const newPin = useMemo(
    () => ({
      name: `Pin ${
        Number(tempGeoConfigs[selectProfileIndex].locations[pinsLength - 1].name.split(' ')[1]) + 1
      }`,
      latitude: defaultGeoConfig?.locations[0].latitude ?? 0,
      longitude: defaultGeoConfig?.locations[0].longitude ?? 0,
      radius: defaultGeoConfig?.locations[0].radius ?? 30,
    }),
    [selectProfileIndex, tempGeoConfigs, defaultGeoConfig, pinsLength],
  );

  const mutation = useSaveGeoConfig();
  const { addToast } = useToast();

  const onProfileNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTempGeoConfigs(
      produce(tempGeoConfigs, (draft) => {
        draft[selectProfileIndex].name = e.target.value;
        return draft;
      }),
    );
  };

  const onMouseEnter = (location: PostGeoConfigLocation) => {
    setSelectPin(location);
  };

  const onMouseLeave = () => {
    setSelectPin(undefined);
  };

  const onClickPinCard = (location: PostGeoConfigLocation) => {
    setLastSavedTempGeoConfig(tempGeoConfigs);
    onSelectPin(location);
    setIsAddingPin(false);
    setIsModifyingPin(true);
    setSelectPin(location);
    setEditingPin(location);
  };

  const onAddPin = () => {
    setIsAddingPin(false);
    setEditingPin(undefined);
    setSelectPin(undefined);
  };

  const onSavePin = () => {
    setIsAddingPin(false);
    setIsModifyingPin(false);
    setEditingPin(undefined);
    setSelectPin(undefined);
  };

  const onDeletePin = () => {
    if (selectPinIndex !== undefined) {
      setTempGeoConfigs(
        produce(tempGeoConfigs, (draft) => {
          draft[selectProfileIndex].locations.splice(selectPinIndex, 1);
          setIsAddingPin(false);
          setIsModifyingPin(false);

          return draft;
        }),
      );
    }
  };

  const addAnotherPin = (pin: PostGeoConfigLocation) => {
    setIsAddingPin(true);
    setLastSavedTempGeoConfig(tempGeoConfigs);
    setTempGeoConfigs(
      produce(tempGeoConfigs, (draft) => {
        draft[selectProfileIndex].locations.push(pin);
        return draft;
      }),
    );
    setSelectProfile(
      produce(selectProfile ?? defaultProfile, (draft) => {
        draft.locations.push(pin);
        return draft;
      }),
    );
    onClickPinCard(pin);
    setSelectPin(pin);
    setEditingPin(pin);
  };

  const onCancelModifyPin = () => {
    setIsAddingPin(false);
    setIsModifyingPin(false);
    setEditingPin(undefined);
    setSelectPin(undefined);
    setSelectProfile(lastSavedTempGeoConfig[selectProfileIndex]);
    setTempGeoConfigs(lastSavedTempGeoConfig);
  };

  const onSaveProfile = () => {
    const isNameValid =
      geoConfigProfileNames.filter((name) => name === tempGeoConfigs[selectProfileIndex].name)
        .length === 1;
    setErrorMessage(
      produce(errorMessage, (draft) => {
        if (isAddingPin && pinsLength === 1) {
          draft.atLeastOnePin = ProfileErrorMessages.atLeastOnePin;
        }
        if (isAddingPin) {
          draft.addPinBeforeSaveProfile = ProfileErrorMessages.addPinBeforeSaveProfile;
        }
        if (tempGeoConfigs[selectProfileIndex].name.length < 1) {
          draft.profileName = 'Required';
        }
        if (!isNameValid) {
          draft.profileName = ProfileErrorMessages.profileName;
        }
        if (tempGeoConfigs[selectProfileIndex].locations.length < 1) {
          draft.atLeastOnePin = ProfileErrorMessages.atLeastOnePin;
        }
        return draft;
      }),
    );
    if (
      tempGeoConfigs[selectProfileIndex].name.length > 0 &&
      isNameValid &&
      !isAddingPin &&
      tempGeoConfigs[selectProfileIndex].locations.length > 0 &&
      Object.keys(errorMessage).length === 0
    ) {
      mutation.mutate(tempGeoConfigs[selectProfileIndex], {
        onSuccess: () => {
          refetch();
          addReactGA('vm_geo_config_save');
          addToast({ status: 'success', description: 'Changes saved!' });
          onSaveCreateNewProfile();
          setCreatingNewProfile(false);
        },
        onError: () => {
          addToast({ status: 'error', description: 'Failed to save – please try again' });
        },
      });
    }
  };

  return (
    <div className={styles.newProfileSidebar}>
      <div className={styles.newProfileHeader}>
        <Text color='secondary' size='20' fontWeight={7}>
          Create new profile
        </Text>
        <IconButton aria-label='close' size='md' onClick={onCloseCreateNewProfile}>
          <IconX size={18} />
        </IconButton>
      </div>
      <div className={styles.newProfileContent}>
        <TextInputField
          label='Profile name'
          id='Profile name'
          variants={errorMessage.profileName ? 'error' : 'default'}
          value={selectProfile?.name ?? defaultProfile.name}
          placeholder='Name of your profile'
          onChange={(e) => {
            onProfileNameChange(e);
            setErrorMessage(
              produce(errorMessage, (draft) => {
                delete draft.profileName;
                return draft;
              }),
            );
          }}
          maxLength={25}
          rightElement={<div className={styles.nameLength}>{profileNameLength}/25</div>}
          message={errorMessage.profileName ? errorMessage.profileName : ' '}
          className={styles.textInput}
        />
        <div className={styles.newProfileContainer}>
          <Text color='primary' size='16' fontWeight={4}>
            Location settings
          </Text>
          <Text color='tertiary' size='12' fontWeight={4}>
            Edit the latitude, longitude and radius values below. You can also drag the pin to its
            desired location on the map.
          </Text>
          <div className={styles.pinList}>
            {tempGeoConfigs[selectProfileIndex].locations.map((location, index) =>
              editingPin?.name === location.name && (isAddingPin || isModifyingPin) ? (
                <CreatNewPin
                  key={index}
                  editingPin={editingPin}
                  setEditingPin={setEditingPin}
                  isModifying={isModifyingPin}
                  errorMessage={errorMessage}
                  pinsLength={pinsLength}
                  onLatChange={onLatChange}
                  onLongChange={onLongChange}
                  onRadiusChange={onRadiusChange}
                  onAddPin={onAddPin}
                  onSavePin={onSavePin}
                  onCancelModifyPin={onCancelModifyPin}
                  onDeletePin={onDeletePin}
                />
              ) : (
                <PinCard
                  key={index}
                  pinName={location.name}
                  onClick={() => onClickPinCard(location)}
                  onMouseEnter={() => onMouseEnter(location)}
                  onMouseLeave={() => onMouseLeave()}
                />
              ),
            )}
            {isAddingPin && errorMessage.addPinBeforeSaveProfile && !errorMessage.atLeastOnePin && (
              <Message category='danger' variant='muted'>
                {errorMessage.addPinBeforeSaveProfile}
              </Message>
            )}
          </div>
          {!isAddingPin && !isModifyingPin && (
            <Button
              variant='outline'
              leftIcon={<PlusIcon />}
              size='md'
              onClick={() => addAnotherPin(newPin)}
              className={styles.addAnotherPin}
            >
              Add another pin
            </Button>
          )}
        </div>
      </div>
      <div className={styles.newProfileFooter}>
        {tempGeoConfigs[selectProfileIndex].locations.length > 0 && isModifyingPin ? (
          <Button variant='outline' onClick={onCancelModifyPin}>
            Cancel
          </Button>
        ) : (
          <Button variant='outline' onClick={onCloseCreateNewProfile}>
            Discard
          </Button>
        )}
        <Button variant='solid' onClick={onSaveProfile}>
          Save profile
        </Button>
      </div>
    </div>
  );
}
